import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";

// --- SOUS-COMPOSANT : Traitement Dispatch ---
const DispatchView = ({ token }) => {
  const [playlists, setPlaylists] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [targetIds, setTargetIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    
    fetch("/api/playlists", {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      if (!res.ok) throw new Error("Erreur chargement playlists");
      return res.json();
    })
    .then(data => {
      if (Array.isArray(data)) {
        setPlaylists(data);
      } else {
        console.error("Format de réponse invalide:", data);
        setPlaylists([]);
      }
    })
    .catch(err => {
      console.error(err);
      setError("Impossible de charger les playlists. Vérifiez la connexion Spotify.");
      setPlaylists([]);
    });
  }, [token]);

  const toggleTarget = (id) => {
    setTargetIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleLaunch = () => {
    setLoading(true);
    setError("");
    setResult("Simulation: Dispatch lancé !");
    setTimeout(() => setLoading(false), 1000);
  };

  return (
    <div style={{ textAlign: "left", marginTop: "20px", padding: "20px", border: "1px solid #eee", borderRadius: "10px", background: "white" }}>
      <h3 style={{color: "#333"}}>🤖 Dispatch Playlist (IA)</h3>
      
      {error && <div style={{color: "red", marginBottom: "10px"}}>{error}</div>}

      <div style={{ marginBottom: "15px" }}>
        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#333" }}>1. Playlist à analyser (Source) :</label>
        <select 
            style={{ width: "100%", padding: "8px" }} 
            onChange={(e) => setSourceId(e.target.value)}
            value={sourceId}
        >
          <option value="">-- Choisir une playlist --</option>
          {playlists.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: "15px" }}>
        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#333" }}>2. Playlists cibles :</label>
        <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ccc", padding: "10px", color: "#333" }}>
          {playlists.length > 0 ? playlists.map(p => (
            <div key={`target-${p.id}`}>
              <input 
                type="checkbox" 
                checked={targetIds.includes(p.id)}
                onChange={() => toggleTarget(p.id)}
              /> {p.name}
            </div>
          )) : <div>Aucune playlist trouvée</div>}
        </div>
      </div>

      <button 
        onClick={handleLaunch} 
        disabled={!sourceId || targetIds.length === 0 || loading}
        style={{ width: "100%", padding: "10px", backgroundColor: "#1DB954", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}
      >
        {loading ? "Traitement..." : "Lancer le Dispatch"}
      </button>
      {result && <p style={{color: "#333"}}>{result}</p>}
    </div>
  );
};

// --- SOUS-COMPOSANT : Derniers Ajouts ---
const LastAddedView = ({ token }) => {
  const [status, setStatus] = useState("");
  const [playlists, setPlaylists] = useState([]); // Toutes les playlists Spotify
  const [selectedPlaylists, setSelectedPlaylists] = useState({}); // IDs cochés { "id1": true, "id2": false }
  
  // NOUVEAUX ÉTATS POUR LA CONFIGURATION
  const [savedConfigs, setSavedConfigs] = useState([]); // Liste des configs depuis la DB
  const [targetName, setTargetName] = useState("Derniers Ajouts"); // Valeur de la Combo Box
  const [targetSize, setTargetSize] = useState(20); // Valeur de X
  
  const [loading, setLoading] = useState(false);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);

  // Charger les configurations existantes au montage
  useEffect(() => {
    fetchConfigs();
  }, [token]);

  const fetchConfigs = async () => {
    try {
      const res = await fetch("/api/playlists/configurations", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSavedConfigs(data);
      }
    } catch (err) {
      console.error("Erreur chargement configs:", err);
    }
  };

  // Charger les playlists Spotify au clic sur le bouton
  const loadPlaylists = async () => {
    if (playlists.length > 0) return; 
    
    setLoadingPlaylists(true);
    try {
      const res = await fetch("/api/playlists/detailed", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Erreur chargement playlists");
      const data = await res.json();
      setPlaylists(data);
      
      // Une fois les playlists chargées, on applique la config actuelle (par défaut ou sélectionnée)
      applyConfigToSelection(targetName, data);

    } catch (err) {
      console.error(err);
      setStatus("❌ Erreur chargement playlists: " + err.message);
    } finally {
      setLoadingPlaylists(false);
    }
  };

  // Fonction magique : Applique une config (taille + coches) quand on change le nom
  const handleConfigNameChange = (newName) => {
    setTargetName(newName);
    
    // Chercher si ce nom existe dans nos configs sauvegardées
    const config = savedConfigs.find(c => c.name === newName);
    
    if (config) {
      // 1. Mettre à jour la taille
      setTargetSize(config.max_tracks);
      
      // 2. Mettre à jour les sélections (uniquement si on a déjà chargé la liste des playlists)
      if (playlists.length > 0) {
        const newSelection = {};
        // On ne coche que ce qui est dans la config
        config.selected_sources.forEach(id => {
            newSelection[id] = true;
        });
        setSelectedPlaylists(newSelection);
      }
    } 
    // Si nouvelle config (pas trouvé), on garde les sélections actuelles ou on peut reset (au choix)
  };

  // Helper pour appliquer la sélection quand on charge les playlists pour la première fois
  const applyConfigToSelection = (name, loadedPlaylists) => {
      const config = savedConfigs.find(c => c.name === name);
      if (config) {
          const newSelection = {};
          config.selected_sources.forEach(id => newSelection[id] = true);
          setSelectedPlaylists(newSelection);
      }
  };

  // Sauvegarder les sélections ET la configuration
  const saveSelections = async () => {
    try {
      const selectionsToSave = {};
      playlists.forEach(p => {
        if (selectedPlaylists[p.id]) {
          selectionsToSave[p.id] = {
            name: p.name,
            owner: p.owner_name
          };
        }
      });
      
      // Payload mis à jour avec target_name et target_size
      const payload = {
          selections: selectionsToSave,
          target_name: targetName,
          target_size: parseInt(targetSize)
      };

      const res = await fetch("/api/playlists/save_selections", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (res.ok) {
        setStatus("✅ Configuration '" + targetName + "' sauvegardée !");
        // Recharger la liste des configs pour que la nouvelle apparaisse dans la liste
        fetchConfigs();
      } else {
        setStatus("❌ " + (data.message || "Erreur sauvegarde"));
      }
    } catch (err) {
      setStatus("❌ Erreur: " + err.message);
    }
  };

  const togglePlaylist = (playlistId) => {
    setSelectedPlaylists(prev => ({
      ...prev,
      [playlistId]: !prev[playlistId]
    }));
  };

  const selectAll = () => {
    const allSelected = {};
    playlists.forEach(p => {
      allSelected[p.id] = true;
    });
    setSelectedPlaylists(allSelected);
  };

  const deselectAll = () => {
    setSelectedPlaylists({});
  };

  const runScript = async () => {
    setLoading(true);
    setStatus("⏳ Calcul en cours pour '" + targetName + "'...");
    try {
      // On envoie aussi le target_name pour que le backend sache quoi lancer
      const res = await fetch("/api/run_last_added", {
        method: "POST",
        headers: { 
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ target_name: targetName })
      });
      const data = await res.json();
      if(res.ok) setStatus("✅ " + data.message);
      else setStatus("❌ " + (data.message || "Erreur serveur"));
    } catch (err) {
      setStatus("❌ Erreur : " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Compteurs
  const selectedCount = Object.values(selectedPlaylists).filter(Boolean).length;
  const ownedCount = playlists.filter(p => p.is_owned_by_user).length;

  return (
    <div style={{ marginTop: "20px", padding: "20px", border: "1px solid #eee", borderRadius: "10px", background: "white", color: "#333" }}>
      <h3>📅 Recalcul "Derniers Ajouts"</h3>
      
      {/* --- NOUVELLE ZONE DE CONFIGURATION --- */}
      <div style={{ marginBottom: "20px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #e9ecef" }}>
        <label style={{ display: "block", marginBottom: "10px", fontWeight: "bold" }}>
          Crée / Met à jour la playlist ci-dessus (Par défaut : 'Derniers Ajouts') avec les X dernières musiques ajoutées dans les playlists ci-dessous
        </label>
        
        <div style={{ display: "flex", gap: "15px", alignItems: "flex-end" }}>
            {/* Combo Box pour le Nom */}
            <div style={{ flex: 2 }}>
                <label style={{ fontSize: "12px", color: "#666", display: "block", marginBottom: "5px" }}>Nom de la Playlist Cible</label>
                <input 
                    list="configOptions" 
                    value={targetName}
                    onChange={(e) => handleConfigNameChange(e.target.value)}
                    placeholder="Ex: Derniers Ajouts"
                    style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                />
                <datalist id="configOptions">
                    {savedConfigs.map((conf, idx) => (
                        <option key={idx} value={conf.name} />
                    ))}
                    {/* Fallback si vide */}
                    {!savedConfigs.find(c => c.name === "Derniers Ajouts") && <option value="Derniers Ajouts" />}
                </datalist>
            </div>

            {/* Input pour X */}
            <div style={{ flex: 1 }}>
                <label style={{ fontSize: "12px", color: "#666", display: "block", marginBottom: "5px" }}>Nombre de titres (X)</label>
                <input 
                    type="number" 
                    min="1" 
                    max="500"
                    value={targetSize}
                    onChange={(e) => setTargetSize(e.target.value)}
                    style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                />
            </div>
        </div>
      </div>

      <button 
        onClick={loadPlaylists}
        style={{ 
          padding: "10px 20px", 
          backgroundColor: "#4CAF50", 
          color: "white", 
          border: "none", 
          borderRadius: "5px", 
          cursor: "pointer",
          marginBottom: "15px"
        }}
        disabled={loadingPlaylists}
      >
        {loadingPlaylists ? "Chargement des playlists..." : "📋 Afficher mes playlists"}
      </button>

      {playlists.length > 0 && (
        <div style={{ marginTop: "20px" }}>
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            marginBottom: "15px",
            padding: "10px",
            backgroundColor: "#f5f5f5",
            borderRadius: "5px"
          }}>
            <div>
              <strong>{playlists.length} playlists trouvées</strong> 
              ({ownedCount} créées par vous)
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={selectAll} style={{ padding: "5px 10px", fontSize: "12px" }}>
                Tout sélectionner
              </button>
              <button onClick={deselectAll} style={{ padding: "5px 10px", fontSize: "12px" }}>
                Tout désélectionner
              </button>
              <button onClick={saveSelections} style={{ 
                padding: "5px 10px", 
                fontSize: "12px",
                backgroundColor: "#2196F3",
                color: "white",
                border: "none",
                fontWeight: "bold"
              }}>
                💾 Sauvegarder Config
              </button>
            </div>
          </div>

          <div style={{ 
            maxHeight: "400px", 
            overflowY: "auto",
            border: "1px solid #ddd",
            borderRadius: "5px"
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ backgroundColor: "#f0f0f0", position: "sticky", top: 0 }}>
                <tr>
                  <th style={{ padding: "10px", textAlign: "left", width: "50px" }}></th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Nom de la playlist</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Créateur</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>Morceaux</th>
                </tr>
              </thead>
              <tbody>
                {playlists.map((playlist, index) => (
                  <tr 
                    key={playlist.id} 
                    style={{ 
                      backgroundColor: playlist.is_owned_by_user ? "#e8f5e9" : "white",
                      borderBottom: "1px solid #eee"
                    }}
                  >
                    <td style={{ padding: "10px", textAlign: "center" }}>
                      <input 
                        type="checkbox" 
                        value={playlist.id}
                        checked={!!selectedPlaylists[playlist.id]}
                        onChange={() => togglePlaylist(playlist.id)}
                        style={{ 
                          transform: "scale(1.3)",
                          cursor: "pointer"
                        }}
                      />
                    </td>
                    <td style={{ padding: "10px" }}>
                      <div style={{ fontWeight: playlist.is_owned_by_user ? "bold" : "normal" }}>
                        {playlist.name}
                        {playlist.is_owned_by_user && (
                          <span style={{ 
                            marginLeft: "8px", 
                            fontSize: "0.8em", 
                            backgroundColor: "#4CAF50",
                            color: "white",
                            padding: "2px 6px",
                            borderRadius: "10px"
                          }}>
                            VOTRE
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "10px" }}>
                      {playlist.owner_name}
                    </td>
                    <td style={{ padding: "10px" }}>
                      {playlist.tracks_total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ 
            marginTop: "15px", 
            padding: "10px", 
            backgroundColor: selectedCount > 0 ? "#e3f2fd" : "#fff3cd",
            borderRadius: "5px"
          }}>
            <strong>
              {selectedCount > 0 
                ? `✅ ${selectedCount} playlist(s) utilisée(s) pour "${targetName}"`
                : "⚠️ Aucune playlist sélectionnée"}
            </strong>
          </div>
        </div>
      )}

      <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
        <button 
          onClick={runScript}
          disabled={selectedCount === 0 || loading}
          style={{ 
            padding: "12px 24px", 
            backgroundColor: selectedCount > 0 ? "#2196F3" : "#cccccc",
            color: "white", 
            border: "none", 
            borderRadius: "5px", 
            cursor: selectedCount > 0 ? "pointer" : "not-allowed",
            fontWeight: "bold"
          }}
        >
          {loading ? "⏳ Traitement..." : `🚀 Lancer la mise à jour de "${targetName}"`}
        </button>
      </div>
      
      {status && (
        <div style={{ 
          marginTop: "15px", 
          padding: "10px", 
          backgroundColor: status.includes("✅") ? "#d4edda" : "#f8d7da",
          color: status.includes("✅") ? "#155724" : "#721c24",
          borderRadius: "5px"
        }}>
          <strong>{status}</strong>
        </div>
      )}
    </div>
  );
};

// --- MAIN PAGE ---
function SpotifyPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(null);

  const handleAuthSpotify = async () => {
    const res = await fetch("/api/get_refresh_token", {
      headers: { 
        Authorization: `Bearer ${token}`
      }
    });
    const data = await res.json();
    if (data.auth_url) window.location.href = data.auth_url;
  };

  const menuBtnStyle = (isActive) => ({
    padding: "15px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    background: isActive ? "#e3f2fd" : "white",
    cursor: "pointer",
    color: "#333",
    fontWeight: "bold",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px"
  });

  return (
    <Layout>
      <div style={{ textAlign: "center" }}>
        
        <div style={{ marginBottom: "30px", borderBottom: "1px solid #eee", paddingBottom: "20px" }}>
          <h1 style={{ color: "#1DB954" }}>🎵 Spotify Manager</h1>
          <button 
            onClick={handleAuthSpotify}
            style={{ backgroundColor: "#191414", color: "white", padding: "10px 20px", borderRadius: "20px", border: "none", cursor: "pointer" }}
          >
            🔑 (Re)Connexion Spotify
          </button>
        </div>

        <h3>Outils Disponibles</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "15px", marginBottom: "30px" }}>
          
          <button onClick={() => setActiveTab('last_added')} style={menuBtnStyle(activeTab === 'last_added')}>
            <span style={{fontSize: "24px"}}>📅</span>
            <span>Recalcul Derniers Ajouts</span>
          </button>

          <button onClick={() => setActiveTab('dispatch')} style={menuBtnStyle(activeTab === 'dispatch')}>
            <span style={{fontSize: "24px"}}>🤖</span>
            <span>Dispatch IA</span>
          </button>

          {/* Boutons inactifs */}
          {[1, 2, 3, 4].map(i => (
            <button key={i} disabled style={{ ...menuBtnStyle(false), background: "#f5f5f5", color: "#aaa", cursor: "default" }}>
              <span style={{fontSize: "24px"}}>🚧</span>
              <span>Bientôt...</span>
            </button>
          ))}
        </div>

        <div style={{ minHeight: "200px" }}>
            {activeTab === 'last_added' && <LastAddedView token={token} />}
            {activeTab === 'dispatch' && <DispatchView token={token} />}
            {!activeTab && <p style={{ color: "#666" }}>Sélectionne un outil ci-dessus.</p>}
        </div>

      </div>
    </Layout>
  );
}

export default SpotifyPage;